// Ported verbatim from the design/rev3 prototype (data.js:82-87, shell.jsx:54-93).
// Labels and colour values are kept identical to the prototype for pixel parity.

export interface StatusDef {
  label: string
  cls: string
}

// data.js:82-87
export const STATUS = {
  online: { label: 'Online', cls: 'online' },
  offline: { label: 'Offline', cls: 'offline' },
  warn: { label: 'Warnung', cls: 'warn' },
  idle: { label: 'Inaktiv', cls: 'idle' },
  // #92: pausierter Agent (isActive=false) — eigenes Label, gleiche graue Optik
  // wie 'idle'. Filter/Zähler bleiben 3-wertig (displayStatus unverändert); nur
  // die Anzeige wählt 'paused', wenn der Agent bewusst pausiert ist.
  paused: { label: 'Pausiert', cls: 'idle' },
} as const satisfies Record<string, StatusDef>

export type StatusKey = keyof typeof STATUS

export interface ColourDef {
  label: string
  fg: string
  bg: string
}

// shell.jsx:86-92 — case status (uses fg/bg inline colours)
// Color-Coding nach #15 (logischer Ablauf): Offen=neutrales Grau,
// In Bearbeitung=Orange, Online=blasses Pastellgrün, Extern geschlossen=Lila,
// Geschlossen=sattes Dunkelgrün. Eine Quelle für Stepper (CaseDetail), Pills
// und Dashboard (#24).
export const CASE_STATUS = {
  offen: { label: 'Offen', fg: 'var(--ink-70)', bg: 'var(--line-strong)' },
  bearbeitung: { label: 'In Bearbeitung', fg: 'var(--st-warn)', bg: 'var(--st-warn-soft)' },
  ticket: { label: 'Ticketsystem', fg: 'oklch(0.55 0.07 250)', bg: 'oklch(0.55 0.07 250 / 0.14)' },
  resolved: { label: 'Gelöst', fg: 'var(--st-online)', bg: 'var(--st-online-soft)' },
  extern: { label: 'Extern geschlossen', fg: 'oklch(0.52 0.06 312)', bg: 'oklch(0.52 0.06 312 / 0.14)' },
  closed: { label: 'Geschlossen', fg: 'oklch(0.45 0.07 160)', bg: 'oklch(0.45 0.07 160 / 0.16)' },
} as const satisfies Record<string, ColourDef>

export type CaseStatusKey = keyof typeof CASE_STATUS

// Maps a backend case-status value to a CASE_STATUS key.
// Backend uses `externGeschlossen`; the prototype key is `extern`. Others pass through.
export function caseStatusKey(apiStatus: string): string {
  if (apiStatus === 'externGeschlossen') return 'extern'
  return apiStatus
}

// shell.jsx:54-62 — functional roles of a technician (multi-select)
export const FUNCTIONS = {
  ops: { label: 'Operations', fg: 'var(--viridian)', bg: 'var(--st-online-soft)' },
  infra: { label: 'Infrastructure', fg: 'var(--copper)', bg: 'var(--st-warn-soft)' },
  consultant: { label: 'Consultant', fg: 'var(--bayleaf)', bg: 'var(--bayleaf-soft)' },
  support: { label: 'Support', fg: 'var(--portugal)', bg: 'var(--portugal-soft)' },
  admin: { label: 'Administrator', fg: 'var(--woodland)', bg: 'var(--woodland-soft)' },
  kundenbetreuer: { label: 'Kundenbetreuer', fg: 'oklch(0.55 0.07 250)', bg: 'oklch(0.55 0.07 250 / 0.14)' },
  vertrieb: { label: 'Vertriebsberater', fg: 'oklch(0.55 0.08 330)', bg: 'oklch(0.55 0.08 330 / 0.14)' },
} as const satisfies Record<string, ColourDef>

export type FunctionKey = keyof typeof FUNCTIONS

// shell.jsx:70-78 — server tags (multi)
export const TAGS = {
  kunde: { label: 'Kundenserver', fg: 'var(--viridian)', bg: 'var(--st-online-soft)' },
  intern: { label: 'Intern', fg: 'oklch(0.55 0.07 250)', bg: 'oklch(0.55 0.07 250 / 0.14)' },
  prod: { label: 'Produktiv', fg: 'var(--woodland)', bg: 'var(--woodland-soft)' },
  test: { label: 'Test/Staging', fg: 'var(--taupe)', bg: 'var(--st-idle-soft)' },
  db: { label: 'Datenbank', fg: 'oklch(0.55 0.08 300)', bg: 'oklch(0.55 0.08 300 / 0.14)' },
  web: { label: 'Webserver', fg: 'var(--bayleaf)', bg: 'var(--bayleaf-soft)' },
  kritisch: { label: 'Kritisch', fg: 'var(--portugal)', bg: 'var(--portugal-soft)' },
  backup: { label: 'Backup', fg: 'var(--copper)', bg: 'var(--st-warn-soft)' },
} as const satisfies Record<string, ColourDef>

export type TagKey = keyof typeof TAGS

// Maintenance kinds (for later use).
export const MAINT_KIND = {
  manual: { label: 'Manuell' },
  planned: { label: 'Geplant' },
  recurring: { label: 'Wiederkehrend' },
} as const satisfies Record<string, { label: string }>

export type MaintKind = keyof typeof MAINT_KIND
